/**
 * SplitDetailsPanel Component
 *
 * Displays a breakdown panel showing split transaction entry allocations.
 * Shows each debit entry (allocation) with icon, account name, optional label, and amount.
 *
 * @see SplitBadge for the compact badge/toggle component
 */

import { PieChart, Home, Percent } from 'lucide-react';
import clsx from 'clsx';
import type { Entry } from '@/types/accounting';

export interface SplitDetailsPanelProps {
  /** All entries from the transaction - will be filtered to show only debit entries */
  entries: Entry[];
}

/**
 * Get icon for entry based on label content
 * - Home icon for principal payments
 * - Percent icon for interest payments
 * - PieChart icon as default
 */
function getEntryIcon(entry: Entry) {
  if (entry.label?.toLowerCase().includes('principal')) {
    return Home;
  }
  if (entry.label?.toLowerCase().includes('interest')) {
    return Percent;
  }
  return PieChart;
}

/**
 * Format amount in the entry's currency
 */
function formatAmount(cents: number, currency: string): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency,
  }).format(cents / 100);
}

/**
 * SplitDetailsPanel renders a visual breakdown of split transaction allocations.
 *
 * Features:
 * - Purple background theme (bg-purple-50)
 * - Header with PieChart icon and "Split Breakdown" title
 * - Filters to only show debit entries (the expense/liability allocations)
 * - Each entry shows: icon, account name, optional label, formatted amount
 * - Icons based on label content (Home for principal, Percent for interest)
 *
 * @example
 * ```tsx
 * <SplitDetailsPanel entries={transaction.entries} />
 * ```
 */
export function SplitDetailsPanel({ entries }: SplitDetailsPanelProps) {
  // Filter to only show debit entries (the expense/liability allocations)
  const allocationEntries = entries.filter((e) => e.entryType === 'debit');

  if (allocationEntries.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="split-details-panel"
      className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-2"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 text-sm font-medium text-purple-700">
        <PieChart className="w-4 h-4" />
        <span>Split Breakdown</span>
      </div>

      {/* Entry list */}
      <div className="space-y-2">
        {allocationEntries.map((entry) => {
          const Icon = getEntryIcon(entry);
          return (
            <div key={entry.id} className="flex items-center gap-3" data-testid="split-entry">
              {/* Entry indicator icon */}
              <div
                className={clsx(
                  'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0',
                  'bg-purple-100 text-purple-600'
                )}
              >
                <Icon className="w-3 h-3" />
              </div>

              {/* Account name and label */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {entry.accountName}
                </p>
                {entry.label && (
                  <p className="text-xs text-gray-500">{entry.label}</p>
                )}
              </div>

              {/* Amount */}
              <div className="text-sm font-medium text-gray-900 tabular-nums">
                {formatAmount(entry.amountCents, entry.currency)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SplitDetailsPanel;
